import React, {
	useState,
	useEffect,
	useContext,
	createContext,
	useRef,
	MutableRefObject,
} from 'react';
import './Product.css';
import SlButton from '@shoelace-style/shoelace/dist/react/button/index.js';
import SlIcon from '@shoelace-style/shoelace/dist/react/icon/index.js';
import SlSpinner from '@shoelace-style/shoelace/dist/react/spinner/index.js';
import SlAlert from '@shoelace-style/shoelace/dist/react/alert/index.js';
import { useProduct } from './useProduct';
import { useDraftAutoSave } from './useDraftAutoSave';
import {
	Product as ProductTyp,
	ProductAvailability,
	ProductStatus as ProductStatusTyp,
	ProductToSet,
	Condition,
	TextsToLocalize,
	UnprocessableError,
	ServiceUnavailableError,
	FieldError,
	Image,
	LocalizedTexts,
	Category,
	Style,
	Century,
	Material,
	LocaleCode,
	ProductImage,
	Origin,
	transformProductImage,
	validateImageFile,
	PRODUCT_IMAGE_MIN_WIDTH,
	PRODUCT_IMAGE_MIN_HEIGHT,
	ImportedProduct,
} from '@anticatrade/shared';
import { consumeImportedProduct } from '../ImportProduct/useImportProduct';
import { Dialog, Spinner, Link, useDeferredLoading } from '@anticatrade/shared';
import { RemoveBackgroundResponse } from '@anticatrade/admin';
import { appContext, AppContext } from '../../../context/AppContext';
import * as Sentry from '@sentry/react';
import * as icons from '../../../constants/icons';
import { ProductInfo } from './ProductInfo';
import { ProductImages } from './ProductImages';
import { ProductDimensions } from './ProductDimensions';
import { ProductShipping } from './ProductShipping';
import { ProductStatus } from './ProductStatus';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { Heading } from '../../base/Heading/Heading';
import { analytics } from '../../../analytics';

interface ProductContext {
	productID: string | undefined;
	title: string | undefined | TextsToLocalize;
	setTitle: React.Dispatch<React.SetStateAction<string | undefined | TextsToLocalize>>;
	description: string | undefined | TextsToLocalize;
	setDescription: React.Dispatch<React.SetStateAction<string | undefined | TextsToLocalize>>;
	price: number | undefined;
	setPrice: React.Dispatch<React.SetStateAction<number | undefined>>;
	conditionID: number | undefined;
	setConditionID: React.Dispatch<React.SetStateAction<number | undefined>>;
	availability: ProductAvailability;
	setAvailability: React.Dispatch<React.SetStateAction<ProductAvailability>>;
	visible: boolean;
	setVisible: React.Dispatch<React.SetStateAction<boolean>>;
	originalImage: Image | undefined;
	setOriginalImage: React.Dispatch<React.SetStateAction<Image | undefined>>;
	image: Image | undefined;
	setImage: React.Dispatch<React.SetStateAction<Image | undefined>>;
	gallery: Image[] | undefined;
	setGallery: React.Dispatch<React.SetStateAction<Image[] | undefined>>;
	width: number | undefined;
	setWidth: React.Dispatch<React.SetStateAction<number | undefined>>;
	length: number | undefined;
	setLength: React.Dispatch<React.SetStateAction<number | undefined>>;
	depth: number | undefined;
	setDepth: React.Dispatch<React.SetStateAction<number | undefined>>;
	height: number | undefined;
	setHeight: React.Dispatch<React.SetStateAction<number | undefined>>;
	diameter: number | undefined;
	setDiameter: React.Dispatch<React.SetStateAction<number | undefined>>;
	sku: string | undefined;
	setSku: React.Dispatch<React.SetStateAction<string | undefined>>;
	shippingZoneA: number | undefined;
	setShippingZoneA: React.Dispatch<React.SetStateAction<number | undefined>>;
	shippingZoneB: number | undefined;
	setShippingZoneB: React.Dispatch<React.SetStateAction<number | undefined>>;
	shippingZoneC: number | undefined;
	setShippingZoneC: React.Dispatch<React.SetStateAction<number | undefined>>;
	shippingZoneD: number | undefined;
	setShippingZoneD: React.Dispatch<React.SetStateAction<number | undefined>>;
	shippingZoneE: number | undefined;
	setShippingZoneE: React.Dispatch<React.SetStateAction<number | undefined>>;
	shippingZoneAOnRequest: boolean;
	setShippingZoneAOnRequest: React.Dispatch<React.SetStateAction<boolean>>;
	shippingZoneBOnRequest: boolean;
	setShippingZoneBOnRequest: React.Dispatch<React.SetStateAction<boolean>>;
	shippingZoneCOnRequest: boolean;
	setShippingZoneCOnRequest: React.Dispatch<React.SetStateAction<boolean>>;
	shippingZoneDOnRequest: boolean;
	setShippingZoneDOnRequest: React.Dispatch<React.SetStateAction<boolean>>;
	shippingZoneEOnRequest: boolean;
	setShippingZoneEOnRequest: React.Dispatch<React.SetStateAction<boolean>>;
	categoryID: number | undefined;
	setCategoryID: React.Dispatch<React.SetStateAction<number | undefined>>;
	centuryID: number | undefined;
	setCenturyID: React.Dispatch<React.SetStateAction<number | undefined>>;
	styleID: number | undefined;
	setStyleID: React.Dispatch<React.SetStateAction<number | undefined>>;
	authorID: number | undefined;
	setAuthorID: React.Dispatch<React.SetStateAction<number | undefined>>;
	originID: number | undefined;
	setOriginID: React.Dispatch<React.SetStateAction<number | undefined>>;
	proposedAuthor: string | undefined;
	setProposedAuthor: React.Dispatch<React.SetStateAction<string | undefined>>;
	materialsIDs: number[] | undefined;
	setMaterialsIDs: React.Dispatch<React.SetStateAction<number[] | undefined>>;
	categories: Category[] | undefined;
	conditions: Condition[] | undefined;
	styles: Style[] | undefined;
	centuries: Century[] | undefined;
	authorName: string | undefined;
	setAuthorName: React.Dispatch<React.SetStateAction<string | undefined>>;
	origins: Origin[] | undefined;
	materials: Material[] | undefined;
	retrievedProduct: ProductTyp | undefined;
	productStatus: ProductStatusTyp | undefined;
	productProcessingError: string | null | undefined;
	imageReprocessing: boolean;
	isLoadingImages: boolean;
	isProcessingMainImage: boolean;
	setIsProcessingMainImage: React.Dispatch<React.SetStateAction<boolean>>;
	galleryProcessingCount: number;
	setGalleryProcessingCount: React.Dispatch<React.SetStateAction<number>>;
	useOriginalImage: boolean;
	setUseOriginalImage: React.Dispatch<React.SetStateAction<boolean>>;
	bgRemovalUnavailable: boolean;
	setBgRemovalUnavailable: React.Dispatch<React.SetStateAction<boolean>>;
	objectURLs: MutableRefObject<string[]>;
	setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
	error: FieldError | undefined;
	setError: React.Dispatch<React.SetStateAction<FieldError | undefined>>;
	isCreation: boolean;
	showTranslations: boolean;
}

const productContext = createContext<ProductContext | undefined>(undefined);

const Product = () => {
	const [title, setTitle] = useState<string | TextsToLocalize>();
	const [description, setDescription] = useState<string | TextsToLocalize>();
	const [price, setPrice] = useState<number>();
	const [conditionID, setConditionID] = useState<number>();
	const [availability, setAvailability] = useState<ProductAvailability>('available');
	const [visible, setVisible] = useState<boolean>(true);
	const [originalImage, setOriginalImage] = useState<Image>(); // The original uploaded image, before the background removal.
	const [image, setImage] = useState<Image>();
	const [gallery, setGallery] = useState<Image[]>();
	const [width, setWidth] = useState<number>();
	const [length, setLength] = useState<number>();
	const [depth, setDepth] = useState<number>();
	const [height, setHeight] = useState<number>();
	const [diameter, setDiameter] = useState<number>();
	const [sku, setSku] = useState<string>();
	const [shippingZoneA, setShippingZoneA] = useState<number>();
	const [shippingZoneB, setShippingZoneB] = useState<number>();
	const [shippingZoneC, setShippingZoneC] = useState<number>();
	const [shippingZoneD, setShippingZoneD] = useState<number>();
	const [shippingZoneE, setShippingZoneE] = useState<number>();
	const [shippingZoneAOnRequest, setShippingZoneAOnRequest] = useState<boolean>(false);
	const [shippingZoneBOnRequest, setShippingZoneBOnRequest] = useState<boolean>(false);
	const [shippingZoneCOnRequest, setShippingZoneCOnRequest] = useState<boolean>(false);
	const [shippingZoneDOnRequest, setShippingZoneDOnRequest] = useState<boolean>(false);
	const [shippingZoneEOnRequest, setShippingZoneEOnRequest] = useState<boolean>(false);
	const [categoryID, setCategoryID] = useState<number>();
	const [centuryID, setCenturyID] = useState<number>();
	const [styleID, setStyleID] = useState<number>();
	const [authorID, setAuthorID] = useState<number>();
	const [originID, setOriginID] = useState<number>();
	const [proposedAuthor, setProposedAuthor] = useState<string>();
	const [authorName, setAuthorName] = useState<string>();
	const [materialsIDs, setMaterialsIDs] = useState<number[]>();
	const [isRemoveDialogOpen, setIsRemoveDialogOpen] = useState<boolean>(false);
	const [isLoadingImages, setIsLoadingImages] = useState<boolean>(false);
	const [error, setError] = useState<FieldError>();
	const [isSaving, setIsSaving] = useState<boolean>(false);
	const [isRemoving, setIsRemoving] = useState<boolean>();
	const [isProcessingMainImage, setIsProcessingMainImage] = useState<boolean>(false);
	const [galleryProcessingCount, setGalleryProcessingCount] = useState<number>(0);
	const [useOriginalImage, setUseOriginalImage] = useState<boolean>(false);
	const [bgRemovalUnavailable, setBgRemovalUnavailable] = useState<boolean>(false);
	const [draftId, setDraftId] = useState<number | null>(null);
	const [isSavingDraft, setIsSavingDraft] = useState<boolean>(false);

	const { t, i18n } = useTranslation();
	const [searchParams] = useSearchParams();

	const objectURLs = useRef<string[]>([]);
	const originalGallery = useRef<ProductImage[] | null>(null);

	const { redirect, showStatus, handleError, api } = useContext(
		appContext as React.Context<AppContext>,
	);
	const {
		productID,
		setProductID,
		setProduct,
		removeProduct,
		retrievedProduct,
		productStatus,
		productProcessingError,
		imageReprocessing,
		categories,
		conditions,
		styles,
		centuries,
		origins,
		materials,
		isLoading,
		setIsLoading,
		isCreation,
		showTranslations,
		setShowTranslations,
		confirmProductTexts,
	} = useProduct();

	const returnPage = searchParams.get('returnPage');
	const returnRedirect = returnPage
		? `products?page=${returnPage}#product-${productID}`
		: 'products';
	const returnPath = '/' + returnRedirect;

	const { showSpinner, showContent } = useDeferredLoading(isLoading);

	// Load draft data when ?draft= param is present.
	useEffect(() => {
		const draftParam = searchParams.get('draft');
		if (draftParam == null || !isCreation || isLoading) {
			return;
		}

		const draftIdNum = Number(draftParam);
		if (isNaN(draftIdNum)) {
			return;
		}

		const loadDraft = async () => {
			try {
				const res = await api.productDraft(draftIdNum);
				const fd = res.draft.formData;
				setDraftId(draftIdNum);

				if (fd.title) setTitle(fd.title);
				if (fd.description) setDescription(fd.description);
				if (fd.price != null) setPrice(fd.price);
				if (fd.conditionID != null) setConditionID(fd.conditionID);
				if (fd.availability) setAvailability(fd.availability as ProductAvailability);
				if (fd.visible === false) setVisible(false);
				if (fd.width != null) setWidth(fd.width);
				if (fd.length != null) setLength(fd.length);
				if (fd.depth != null) setDepth(fd.depth);
				if (fd.height != null) setHeight(fd.height);
				if (fd.diameter != null) setDiameter(fd.diameter);
				if (fd.sku) setSku(fd.sku);
				if (fd.shippingZoneA != null) setShippingZoneA(fd.shippingZoneA);
				if (fd.shippingZoneB != null) setShippingZoneB(fd.shippingZoneB);
				if (fd.shippingZoneC != null) setShippingZoneC(fd.shippingZoneC);
				if (fd.shippingZoneD != null) setShippingZoneD(fd.shippingZoneD);
				if (fd.shippingZoneE != null) setShippingZoneE(fd.shippingZoneE);
				if (fd.shippingZoneAOnRequest) setShippingZoneAOnRequest(true);
				if (fd.shippingZoneBOnRequest) setShippingZoneBOnRequest(true);
				if (fd.shippingZoneCOnRequest) setShippingZoneCOnRequest(true);
				if (fd.shippingZoneDOnRequest) setShippingZoneDOnRequest(true);
				if (fd.shippingZoneEOnRequest) setShippingZoneEOnRequest(true);
				if (fd.categoryID != null) setCategoryID(fd.categoryID);
				if (fd.centuryID != null) setCenturyID(fd.centuryID);
				if (fd.styleID != null) setStyleID(fd.styleID);
				if (fd.authorID != null) setAuthorID(fd.authorID);
				if (fd.authorName) setAuthorName(fd.authorName);
				if (fd.originID != null) setOriginID(fd.originID);
				if (fd.proposedAuthor) setProposedAuthor(fd.proposedAuthor);
				if (fd.materialsIDs != null && fd.materialsIDs.length > 0)
					setMaterialsIDs(fd.materialsIDs);
				if (fd.useOriginalImage) setUseOriginalImage(true);

				// Fetch images and convert to File objects.
				if (res.draft.imageKeys.length > 0) {
					const hasMain = fd.hasMainImage === true;
					const hasOriginal = hasMain && fd.hasOriginalImage === true;
					const mainCount = (hasMain ? 1 : 0) + (hasOriginal ? 1 : 0);
					const galleryCount = res.draft.imageKeys.length - mainCount;
					setIsProcessingMainImage(hasMain);
					setGalleryProcessingCount(galleryCount);

					const imageFiles: File[] = [];
					for (const key of res.draft.imageKeys) {
						try {
							const url = `${import.meta.env.VITE_IMAGES_DISTRIBUTION}/${key}`;
							const resp = await fetch(url);
							const blob = await resp.blob();
							const fileName = key.split('/').pop() || 'image.webp';
							imageFiles.push(new File([blob], fileName, { type: blob.type }));
						} catch {
							// Skip images that can't be fetched.
						}
					}
					if (imageFiles.length > 0) {
						let nextIndex = 0;
						if (hasMain) {
							const mainFile = imageFiles[0];
							const mainUrl = URL.createObjectURL(mainFile);
							objectURLs.current.push(mainUrl);
							setImage({ file: mainFile, url: mainUrl, position: null });
							nextIndex = 1;
						}
						if (hasOriginal && nextIndex < imageFiles.length) {
							const origFile = imageFiles[nextIndex];
							const origUrl = URL.createObjectURL(origFile);
							objectURLs.current.push(origUrl);
							setOriginalImage({ file: origFile, url: origUrl, position: null });
							nextIndex++;
						}
						setIsProcessingMainImage(false);

						if (nextIndex < imageFiles.length) {
							const galleryImages: Image[] = [];
							for (let i = nextIndex; i < imageFiles.length; i++) {
								const gUrl = URL.createObjectURL(imageFiles[i]);
								objectURLs.current.push(gUrl);
								galleryImages.push({
									file: imageFiles[i],
									url: gUrl,
									position: i - nextIndex + 1,
								});
							}
							setGallery(galleryImages);
						}
					} else {
						setIsProcessingMainImage(false);
					}
					setGalleryProcessingCount(0);
				}
			} catch {
				// Draft loading failed, continue with empty form.
			}
		};
		loadDraft();
	}, [isCreation, isLoading]);

	useEffect(() => {
		const importParam = searchParams.get('import');
		if (importParam !== 'true' || !isCreation || isLoading) {
			return;
		}

		// Load imported product data.

		const loadImport = async () => {
			let imported: ImportedProduct | null;
			try {
				imported = await consumeImportedProduct();
			} catch (err) {
				Sentry.captureException(err);
				handleError(
					t(
						'Something went wrong, please try again and if the issue persists contact our support',
					),
				);
				return;
			}
			if (imported == null) {
				return;
			}

			if (imported.title) setTitle(imported.title);
			if (imported.description) setDescription(imported.description);
			if (imported.price > 0) setPrice(imported.price);
			if (imported.categoryID != null) setCategoryID(imported.categoryID);
			if (imported.centuryID != null) setCenturyID(imported.centuryID);
			if (imported.styleID != null) setStyleID(imported.styleID);
			if (imported.conditionID != null) setConditionID(imported.conditionID);
			if (imported.originID != null) setOriginID(imported.originID);
			if (imported.materialIDs != null && imported.materialIDs.length > 0) {
				setMaterialsIDs(imported.materialIDs);
			}
			if (imported.dimensions != null) {
				if (imported.dimensions.width != null) setWidth(imported.dimensions.width);
				if (imported.dimensions.height != null) setHeight(imported.dimensions.height);
				if (imported.dimensions.depth != null) setDepth(imported.dimensions.depth);
				if (imported.dimensions.length != null) setLength(imported.dimensions.length);
				if (imported.dimensions.diameter != null) setDiameter(imported.dimensions.diameter);
			}

			if (imported.images && imported.images.length > 0) {
				const galleryCount = imported.images.length - 1;
				setIsProcessingMainImage(true);
				setGalleryProcessingCount(galleryCount > 0 ? galleryCount : 0);

				const locale = i18n.language as LocaleCode;

				// Decode base64 images into File objects.
				const imageFiles: File[] = [];
				for (const img of imported.images) {
					try {
						const byteString = atob(img.data);
						const ab = new ArrayBuffer(byteString.length);
						const ia = new Uint8Array(ab);
						for (let j = 0; j < byteString.length; j++) {
							ia[j] = byteString.charCodeAt(j);
						}
						const blob = new Blob([ab], { type: img.mimeType });
						imageFiles.push(new File([blob], img.fileName, { type: img.mimeType }));
					} catch {
						// Skip images that can't be decoded.
					}
				}

				if (imageFiles.length === 0) {
					setIsProcessingMainImage(false);
					setGalleryProcessingCount(0);
					return;
				}

				// Process main image.
				const mainFile = imageFiles[0];
				let transformed: File | undefined;
				try {
					const t = await transformProductImage(mainFile, locale);
					await validateImageFile(
						t,
						PRODUCT_IMAGE_MIN_WIDTH,
						PRODUCT_IMAGE_MIN_HEIGHT,
						true,
						false,
						locale,
						true,
					);
					transformed = t;
				} catch (err) {
					const er = err as Error;
					setOriginalImage(undefined);
					setError(new FieldError(er.message, 'image'));
				}

				if (transformed != null) {
					const transformedUrl = URL.createObjectURL(transformed);
					objectURLs.current.push(transformedUrl);
					setOriginalImage({
						file: transformed,
						url: transformedUrl,
						position: null,
					});

					// Remove the background.
					try {
						const res: RemoveBackgroundResponse =
							await api.removeBackground(transformed);
						const bgBlob = new Blob(
							[Uint8Array.from(atob(res.image), (c) => c.charCodeAt(0))],
							{ type: 'image/webp' },
						);
						const bgFile = new File([bgBlob], 'background-removed.webp', {
							type: 'image/webp',
						});
						const bgUrl = URL.createObjectURL(bgFile);
						objectURLs.current.push(bgUrl);
						setImage({ file: bgFile, url: bgUrl, position: null });
					} catch (err) {
						if (err instanceof ServiceUnavailableError) {
							// Photoroom outage during import: fall back
							// to the un-cropped image as the only version.
							setImage({ file: transformed, url: transformedUrl, position: null });
							setOriginalImage(undefined);
							setBgRemovalUnavailable(true);
						} else {
							setOriginalImage(undefined);
							handleError(err as Error);
						}
					}
				}

				setIsProcessingMainImage(false);

				// Process gallery images
				if (imageFiles.length > 1) {
					const galleryImages: Image[] = [];
					for (let i = 1; i < imageFiles.length; i++) {
						try {
							const file = await transformProductImage(imageFiles[i], locale);
							await validateImageFile(
								file,
								PRODUCT_IMAGE_MIN_WIDTH,
								PRODUCT_IMAGE_MIN_HEIGHT,
								true,
								false,
								locale,
								true,
							);
							const gUrl = URL.createObjectURL(file);
							objectURLs.current.push(gUrl);
							galleryImages.push({ file, url: gUrl, position: i });
						} catch {
							// Skip images that fail transformation or validation.
						}
					}
					if (galleryImages.length > 0) {
						setGallery(galleryImages);
					}
				}
				setGalleryProcessingCount(0);
			}
		};
		loadImport().catch(() => {
			// Import data loading failed, continue with empty form.
		});
	}, [isCreation, isLoading]);

	// Auto-save draft when navigating away from creation form.
	useDraftAutoSave({
		api,
		isCreation,
		showTranslations,
		draftId,
		setDraftId,
		setSavingDraft: setIsSavingDraft,
		title: typeof title === 'string' ? title : undefined,
		description: typeof description === 'string' ? description : undefined,
		price,
		conditionID,
		availability,
		visible,
		width,
		length,
		depth,
		height,
		diameter,
		sku,
		shippingZoneA,
		shippingZoneB,
		shippingZoneC,
		shippingZoneD,
		shippingZoneE,
		shippingZoneAOnRequest,
		shippingZoneBOnRequest,
		shippingZoneCOnRequest,
		shippingZoneDOnRequest,
		shippingZoneEOnRequest,
		categoryID,
		centuryID,
		styleID,
		authorID,
		authorName,
		originID,
		proposedAuthor,
		materialsIDs,
		useOriginalImage,
		image,
		originalImage,
		gallery,
		isProcessingMainImage,
		galleryProcessingCount,
	});

	useEffect(() => {
		return () => {
			for (const url of objectURLs.current) {
				URL.revokeObjectURL(url);
			}
			objectURLs.current = [];
		};
	}, []);

	useEffect(() => {
		if (retrievedProduct == null) {
			return;
		}
		const setData = async () => {
			// Set the product data.
			const titleTexts: LocalizedTexts = {} as LocalizedTexts;
			for (const lang in retrievedProduct.texts) {
				const l = lang as LocaleCode;
				const t = retrievedProduct.texts[l].title;
				titleTexts[l] = t == null ? '' : t;
			}
			setTitle(titleTexts);

			const description: LocalizedTexts = {} as LocalizedTexts;
			for (const lang in retrievedProduct.texts) {
				const l = lang as LocaleCode;
				const t = retrievedProduct.texts[l].description;
				description[l] = t == null ? '' : t;
			}
			setDescription(description);

			if (isCreation) {
				// We only need the texts to confirm them.
				return;
			}

			setPrice(retrievedProduct.price === 0 ? undefined : retrievedProduct.price);
			setAvailability(retrievedProduct.availability);
			setVisible(retrievedProduct.visible);
			setWidth(retrievedProduct.width == null ? undefined : retrievedProduct.width);
			setLength(retrievedProduct.length == null ? undefined : retrievedProduct.length);
			setDepth(retrievedProduct.depth == null ? undefined : retrievedProduct.depth);
			setHeight(retrievedProduct.height == null ? undefined : retrievedProduct.height);
			setDiameter(retrievedProduct.diameter == null ? undefined : retrievedProduct.diameter);
			setSku(retrievedProduct.sku == null ? undefined : retrievedProduct.sku);
			setShippingZoneA(retrievedProduct.shippingZoneA ?? undefined);
			setShippingZoneB(retrievedProduct.shippingZoneB ?? undefined);
			setShippingZoneC(retrievedProduct.shippingZoneC ?? undefined);
			setShippingZoneD(retrievedProduct.shippingZoneD ?? undefined);
			setShippingZoneE(retrievedProduct.shippingZoneE ?? undefined);
			setShippingZoneAOnRequest(retrievedProduct.shippingZoneAOnRequest);
			setShippingZoneBOnRequest(retrievedProduct.shippingZoneBOnRequest);
			setShippingZoneCOnRequest(retrievedProduct.shippingZoneCOnRequest);
			setShippingZoneDOnRequest(retrievedProduct.shippingZoneDOnRequest);
			setShippingZoneEOnRequest(retrievedProduct.shippingZoneEOnRequest);
			const condition = conditions?.find((c) => c.name === retrievedProduct.condition);
			if (condition != null) {
				setConditionID(condition.id);
			}
			setCategoryID(retrievedProduct.category.id);
			setCenturyID(retrievedProduct.century.id);
			setStyleID(retrievedProduct.style.id);
			if (retrievedProduct.author != null) {
				const isProposed = retrievedProduct.author.isApproved === false;
				if (isProposed) {
					setProposedAuthor(retrievedProduct.author.name);
					setAuthorID(retrievedProduct.author.id);
				} else {
					setAuthorID(retrievedProduct.author.id);
					setAuthorName(retrievedProduct.author.name);
				}
			}
			if (retrievedProduct.origin != null) {
				setOriginID(retrievedProduct.origin.id);
			}
			if (retrievedProduct.materials != null) {
				const mats = [];
				for (let material of retrievedProduct.materials) {
					const m = materials?.find((ma) => ma.name === material);
					if (m != null) {
						mats.push(m.id);
					}
				}
				if (mats.length > 0) {
					setMaterialsIDs(mats);
				}
			}

			// Convert the images in the UI-friendly format.
			setIsLoadingImages(true);

			const isProcessing = retrievedProduct.status === 'processing';
			let imageBaseURL: string;
			if (isProcessing) {
				// During processing, show original uploads.
				imageBaseURL = `${import.meta.env.VITE_PRODUCT_IMAGE_URL.replace(
					/\/products$/,
					'/products-upload',
				)}/${productID}`;
			} else {
				imageBaseURL = `${import.meta.env.VITE_PRODUCT_IMAGE_URL}/${productID}`;
			}

			const img: Image = {
				url: isProcessing
					? `${imageBaseURL}/main-${retrievedProduct.image.fileName}.${retrievedProduct.image.originalExtension}`
					: `${imageBaseURL}/large-${retrievedProduct.image.fileName}.webp`,
				position: null,
			};
			setImage(img);

			originalGallery.current = retrievedProduct.gallery;
			const gallery: Image[] = [];
			for (const img of retrievedProduct.gallery) {
				gallery.push({
					id: img.id,
					url: isProcessing
						? `${imageBaseURL}/${img.fileName}.${img.originalExtension}`
						: `${imageBaseURL}/large-${img.fileName}.webp`,
					position: img.position,
				});
			}
			setGallery(gallery);
			setIsLoadingImages(false);
		};
		setData();
	}, [retrievedProduct, categories, styles, centuries, materials]);

	const onSetProduct = async () => {
		setError(undefined);

		const p = {
			title: title,
			description: description,
			price: price,
			conditionID: conditionID,
			availability: availability,
			visible: visible,
			width: width,
			length: length,
			depth: depth,
			height: height,
			diameter: diameter,
			sku: sku,
			shippingZoneA: shippingZoneA ?? null,
			shippingZoneB: shippingZoneB ?? null,
			shippingZoneC: shippingZoneC ?? null,
			shippingZoneD: shippingZoneD ?? null,
			shippingZoneE: shippingZoneE ?? null,
			shippingZoneAOnRequest: shippingZoneAOnRequest,
			shippingZoneBOnRequest: shippingZoneBOnRequest,
			shippingZoneCOnRequest: shippingZoneCOnRequest,
			shippingZoneDOnRequest: shippingZoneDOnRequest,
			shippingZoneEOnRequest: shippingZoneEOnRequest,
			categoryID: categoryID,
			centuryID: centuryID,
			styleID: styleID,
			authorID: authorID,
			originID: originID,
			proposedAuthor: authorID != null ? null : (proposedAuthor ?? null),
			materialsIDs: materialsIDs,
		};

		let id: number | undefined;
		setIsSaving(true);

		try {
			id = await setProduct(
				p as ProductToSet,
				useOriginalImage ? originalImage : image,
				!useOriginalImage ? originalImage : undefined,
				gallery,
				originalGallery.current,
			);
		} catch (err) {
			setTimeout(() => {
				setIsSaving(false);
				if (err instanceof UnprocessableError) {
					if (err.code === 'MerchantSuspended') {
						handleError(t('Account suspended message'));
						return;
					} else if (err.code === 'SubscriptionExpired') {
						handleError(t('Subscription expired message'));
						return;
					} else if (err.code === 'SubscriptionLimitReached') {
						handleError(t('Subscription limit reached message'));
						return;
					}
				}
				if (err instanceof FieldError) {
					const field = document.querySelector(`#error__field-${err.field}`);
					if (field != null) {
						setError(err);
						field.scrollIntoView({ behavior: 'smooth', block: 'center' });
					}
					return;
				}
				handleError(err as Error);
			}, 300);
			return;
		}
		setIsSaving(false);
		if (isCreation) {
			analytics.track('Product Added', { from_draft: draftId != null });
			// Delete the draft now that the product has been created.
			if (draftId != null) {
				api.deleteProductDraft(draftId).catch(() => {});
				setDraftId(null);
			}
			setShowTranslations(true);
			setProductID(String(id));
		} else {
			analytics.track('Product Edited');
			showStatus({ variant: 'success', icon: icons.OK, text: t('Item saved successfully') });
			redirect(returnRedirect);
			return;
		}
	};

	const onConfirmTexts = async () => {
		setError(undefined);
		setIsSaving(true);
		try {
			await confirmProductTexts(title as LocalizedTexts, description as LocalizedTexts);
		} catch (err) {
			setIsSaving(false);
			if (err instanceof FieldError) {
				const field = document.querySelector(`#error__field-${err.field}`);
				if (field != null) {
					setError(err);
					field.scrollIntoView({ behavior: 'smooth', block: 'center' });
				}
				return;
			}
			handleError(err as Error);
			return;
		}
		setIsSaving(false);
		showStatus({ variant: 'success', icon: icons.OK, text: t('Item saved successfully') });
		redirect('products');
		return;
	};

	const onRemoveProduct = () => {
		setIsRemoveDialogOpen(true);
	};

	const onConfirmRemove = async () => {
		setIsRemoving(true);
		try {
			await removeProduct();
		} catch (err) {
			setIsRemoving(false);
			handleError(err as Error);
			return;
		}
		analytics.track('Product Deleted');
		setIsRemoving(false);
		redirect(returnRedirect);
	};

	const isHandlingImages = isLoadingImages || isProcessingMainImage || galleryProcessingCount > 0;

	return (
		<div className='product'>
			<div className='product__content'>
				{showContent ? (
					<div
						className={`product__main${
							showTranslations ? ' product__main--translations' : ''
						}`}
					>
						<productContext.Provider
							value={{
								productID,
								title,
								setTitle,
								description,
								setDescription,
								price,
								setPrice,
								conditionID,
								setConditionID,
								availability,
								setAvailability,
								visible,
								setVisible,
								originalImage,
								setOriginalImage,
								image,
								setImage,
								gallery,
								setGallery,
								width,
								setWidth,
								length,
								setLength,
								depth,
								setDepth,
								height,
								setHeight,
								diameter,
								setDiameter,
								sku,
								setSku,
								shippingZoneA,
								setShippingZoneA,
								shippingZoneB,
								setShippingZoneB,
								shippingZoneC,
								setShippingZoneC,
								shippingZoneD,
								setShippingZoneD,
								shippingZoneE,
								setShippingZoneE,
								shippingZoneAOnRequest,
								setShippingZoneAOnRequest,
								shippingZoneBOnRequest,
								setShippingZoneBOnRequest,
								shippingZoneCOnRequest,
								setShippingZoneCOnRequest,
								shippingZoneDOnRequest,
								setShippingZoneDOnRequest,
								shippingZoneEOnRequest,
								setShippingZoneEOnRequest,
								categoryID,
								setCategoryID,
								centuryID,
								setCenturyID,
								styleID,
								setStyleID,
								authorID,
								setAuthorID,
								originID,
								setOriginID,
								proposedAuthor,
								setProposedAuthor,
								materialsIDs,
								setMaterialsIDs,
								categories,
								conditions,
								styles,
								centuries,
								authorName,
								setAuthorName,
								origins: origins,
								materials,
								isLoadingImages,
								isProcessingMainImage,
								setIsProcessingMainImage,
								galleryProcessingCount,
								setGalleryProcessingCount,
								useOriginalImage,
								setUseOriginalImage,
								bgRemovalUnavailable,
								setBgRemovalUnavailable,
								retrievedProduct,
								productStatus,
								productProcessingError,
								imageReprocessing,
								objectURLs,
								setIsLoading,
								error,
								setError,
								isCreation,
								showTranslations,
							}}
						>
							<Heading
								left={
									<div className='product__heading'>
										<Link path={returnPath}>
											<SlButton
												className='product__exit-button'
												variant='text'
												size='medium'
											>
												<SlIcon name='arrow-left' slot='prefix' />
											</SlButton>
										</Link>
										<h1 className='product__title'>
											{!isCreation || showTranslations
												? (title as TextsToLocalize)[
														i18n.language as LocaleCode
													]
												: t('Add item')}
										</h1>
									</div>
								}
							/>
							{showTranslations ? (
								<div className='product__translation wrapped'>
									<ProductInfo />
								</div>
							) : (
								<div>
									{(productStatus === 'processing' || imageReprocessing) && (
										<SlAlert
											className='product__processing-alert'
											open
											variant='primary'
										>
											<SlSpinner slot='icon' />
											{t(
												'Images are being processed. The item will be visible in the showcase once processing is complete.',
											)}
										</SlAlert>
									)}
									{(productStatus === 'failed' ||
										(productStatus === 'completed' &&
											productProcessingError != null &&
											!imageReprocessing)) && (
										<SlAlert
											className='product__processing-alert product__processing-alert--failed'
											open
											variant='danger'
										>
											<SlIcon name='exclamation-circle-fill' slot='icon' />
											{productProcessingError ===
											'ImageProcessingInvalidImageError'
												? t(
														'One of the images could not be processed. Please try again and if necessary try using another image',
													)
												: t('ImageProcessingGenericError')}
										</SlAlert>
									)}
									<div className='product__columns'>
										<div className='product__column-left'>
											<ProductInfo />
											<ProductImages />
										</div>
										<div className='product__column-right'>
											<ProductStatus />
											<ProductDimensions />
											<ProductShipping />
										</div>
									</div>
								</div>
							)}
							<div className='product__buttons'>
								{!isCreation && (
									<SlButton
										className='product__remove-button'
										variant='neutral'
										onClick={onRemoveProduct}
									>
										{t('Delete')}
									</SlButton>
								)}
								{showTranslations ? (
									<SlButton
										className='product__save-button'
										variant='primary'
										onClick={isHandlingImages ? undefined : onConfirmTexts}
										loading={isSaving}
										disabled={isHandlingImages}
									>
										{t('Save')}
									</SlButton>
								) : (
									<SlButton
										className='product__save-button'
										variant='primary'
										onClick={isHandlingImages ? undefined : onSetProduct}
										loading={isSaving}
										disabled={
											isHandlingImages ||
											productStatus === 'processing' ||
											imageReprocessing
										}
									>
										{t('Save')}
									</SlButton>
								)}
							</div>
							<Dialog
								variant='danger'
								title={t('Are you sure?')}
								className='product__remove-dialog'
								isOpen={isRemoveDialogOpen}
								onAfterHide={() => setIsRemoveDialogOpen(false)}
								actions={
									<div className='product__remove-dialog-buttons'>
										<SlButton
											variant='neutral'
											onClick={() => setIsRemoveDialogOpen(false)}
										>
											{t('Cancel')}
										</SlButton>
										<SlButton
											variant='danger'
											onClick={onConfirmRemove}
											loading={isRemoving}
										>
											{t('Delete permanently')}
										</SlButton>
									</div>
								}
							>
								{t(
									'If you proceed the item data will be deleted and can no longer be retrieved',
								)}
							</Dialog>
						</productContext.Provider>
					</div>
				) : (
					showSpinner && <Spinner />
				)}
			</div>
			{isSavingDraft && (
				<div className='product__saving-draft-overlay'>
					<SlSpinner
						style={{ fontSize: '55px', '--track-width': '8px' } as React.CSSProperties}
					/>
					<span>{t('Saving draft...')}</span>
				</div>
			)}
		</div>
	);
};

export { Product, productContext };
export type { ProductContext };
